/**
 * Performance Optimization Utilities
 * 
 * This file contains utilities to optimize the Updates section loading
 * and reduce lag on older devices.
 */

// 1. Debounce utility for expensive operations
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// 2. Throttle utility for scroll/resize events
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// 3. Memory-efficient asset processing
export const processAssetsInBatches = async <T>(
  assets: T[],
  processor: (asset: T) => Promise<any>,
  batchSize: number = 5
): Promise<any[]> => {
  const results: any[] = [];
  
  for (let i = 0; i < assets.length; i += batchSize) {
    const batch = assets.slice(i, i + batchSize);
    const batchResults = await Promise.all(
      batch.map(asset => processor(asset))
    );
    results.push(...batchResults);
    
    // Yield control to the browser
    await new Promise(resolve => setTimeout(resolve, 0));
  }
  
  return results;
};

// 4. Smart preloading for social media scripts
export const preloadSocialScripts = () => {
  // Skip preloading entirely to avoid warnings
  // Scripts will be loaded on-demand when needed
  // Logging removed - no action needed for preload skip
};

// 5. Optimize images and media
export const optimizeMediaLoading = () => {
  // Add loading="lazy" to all images
  const images = document.querySelectorAll('img');
  images.forEach(img => {
    if (!img.hasAttribute('loading')) {
      img.setAttribute('loading', 'lazy');
    }
  });

  // Add loading="lazy" to all iframes
  const iframes = document.querySelectorAll('iframe');
  iframes.forEach(iframe => {
    if (!iframe.hasAttribute('loading')) {
      iframe.setAttribute('loading', 'lazy');
    }
  });
};

// 6. Reduce motion for users who prefer it
export const shouldReduceMotion = (): boolean => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// 7. Smart caching for expensive computations
class ComputationCache {
  private cache = new Map<string, { value: any; timestamp: number }>();
  private maxAge = 5 * 60 * 1000; // 5 minutes

  get<T>(key: string, computeFn: () => T): T {
    const cached = this.cache.get(key);
    const now = Date.now();
    
    if (cached && (now - cached.timestamp) < this.maxAge) {
      return cached.value;
    }
    
    const value = computeFn();
    this.cache.set(key, { value, timestamp: now });
    return value;
  }

  clear(): void {
    this.cache.clear();
  }
}

export const computationCache = new ComputationCache();

// 8. Performance budget monitoring
export const checkPerformanceBudget = () => {
  const budget = {
    lcp: 2500,    // 2.5s
    fcp: 1800,    // 1.8s
    cls: 0.1,     // 0.1
    inp: 200,     // 200ms
    memory: 100   // 100MB
  };

  return {
    budget,
    isWithinBudget: (metrics: any) => {
      return (
        (!metrics.LCP || metrics.LCP.value <= budget.lcp) &&
        (!metrics.FCP || metrics.FCP.value <= budget.fcp) &&
        (!metrics.CLS || metrics.CLS.value <= budget.cls) &&
        (!metrics.INP || metrics.INP.value <= budget.inp) &&
        (!metrics['MEMORY-USED'] || metrics['MEMORY-USED'].value <= budget.memory)
      );
    }
  };
};

// 9. Critical resource hints
export const addCriticalResourceHints = () => {
  const hints = [
    { rel: 'dns-prefetch', href: 'https://platform.twitter.com' },
    { rel: 'dns-prefetch', href: 'https://www.instagram.com' },
    { rel: 'dns-prefetch', href: 'https://www.tiktok.com' },
    { rel: 'dns-prefetch', href: 'https://www.youtube.com' },
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' }
  ];

  hints.forEach(hint => {
    const link = document.createElement('link');
    Object.assign(link, hint);
    document.head.appendChild(link);
  });
};

// 10. Initialize all optimizations
export const initializePerformanceOptimizations = () => {
  addCriticalResourceHints();
  preloadSocialScripts();
  optimizeMediaLoading();
  
  // Performance optimizations initialized - no logging needed
};
